import React, { useRef } from 'react'
import { Dropdown } from '@/components/ui/modal';
import { useTranslation } from 'react-i18next';
import { TextInput } from '@/components/common/text-input';
import {
  CalendarActiveIcon,
  CalendarIcon,
  SearchActiveIcon,
  SearchBtnIcon,
  SearchIcon
} from '@/components/agent-dashboard/assets';
import { DatePicker, DatePickerActions } from '@/components/common/date-picker';
import { formatDateToYYYYMMDD } from '@/utils/date-formatter';
import { TextButton } from '@/components/common/text-button';
import { cn } from '@/lib/utils';
import { SearchFormData } from '@/manager/manager/agent-dashboard-operation-log-manager';

interface SearchBarProps {
  searchFormData: SearchFormData,
  search: (page: number) => void;
  resetAndSearch: () => void;
  changeSearchForm: (key: keyof SearchFormData, value: SearchFormData[keyof SearchFormData] | string[]) => void,
}

export const SearchBar = ({
  searchFormData,
  search,
  resetAndSearch,
  changeSearchForm
}: SearchBarProps) => {
  const { t } = useTranslation();

  const datePickerRef = useRef<DatePickerActions>(null);

  const handleReset = () => {
    resetAndSearch();
    datePickerRef.current?.reset();
  }

  const handleSearch = () => {
    search(1);
  }

  return (
    <div className='flex flex-wrap gap-3'>
      <div>
        <label className='text-sm font-medium text-[rgba(52,64,84,1)] mb-1.5 inline-block'>{t('agent_dashboard.operation_log.search_bar.filter.filter_label')}</label>
        <Dropdown
          value={searchFormData.filterType}
          placeholder={t('agent_dashboard.operation_log.search_bar.filter.filter_placeholder')}
          className='h-9'
          defaultValue='name'
          onValueChange={(value) => changeSearchForm('filterType', value)}
          width='11.3rem'
          options={[
            { value: 'user_name', label: t('agent_dashboard.operation_log.search_bar.filter.user_name') },
            { value: 'user_id', label: t('agent_dashboard.operation_log.search_bar.filter.user_id') },
          ]}
        />
      </div>
      <div className='flex items-end'>
        <TextInput
          value={searchFormData.filterKeyword}
          placeholder={t('agent_dashboard.operation_log.search_bar.search_placeholder')}
          className='w-[18.9rem] h-9 font-normal'
          prefixIcon={<SearchIcon className='w-5 h-5' />}
          prefixActiveIcon={<SearchActiveIcon className='w-5 h-5 text-black' />}
          onChange={(e) => changeSearchForm('filterKeyword', e.target.value)}
        />
      </div>
      <div>
        <label className='text-sm font-medium text-[rgba(52,64,84,1)] mb-1.5 inline-block'>{t('agent_dashboard.operation_log.search_bar.operation_module')}</label>
        <Dropdown
          value={searchFormData.moduleType}
          placeholder={t('agent_dashboard.operation_log.search_bar.operation_module_placeholder')}
          className='h-9'
          defaultValue='user_name'
          onValueChange={(value) => changeSearchForm('moduleType', value)}
          width='14rem'
          options={[
            { value: '1', label: t('agent_dashboard.operation_log.module_type_map.user') },
            { value: '3', label: t('agent_dashboard.operation_log.module_type_map.permission') },
            { value: '2', label: t('agent_dashboard.operation_log.module_type_map.order') },
            { value: '4', label: t('agent_dashboard.operation_log.module_type_map.company_asset') },
            // { value: '5', label: t('agent_dashboard.operation_log.module_type_map.basic_settings') },
            // { value: '999', label: t('agent_dashboard.operation_log.module_type_map.other') },
          ]}
        />
      </div>
      <div>
        <label className='text-sm font-medium text-[rgba(52,64,84,1)] mb-1.5 inline-block'>{t('agent_dashboard.operation_log.search_bar.start_date')}</label>
        <DatePicker
          range
          ref={datePickerRef}
          startTrigger={(
            <TextInput
              readOnly
              value={searchFormData.startDate}
              prefixIcon={<CalendarIcon className='w-5 h-5' />}
              prefixActiveIcon={<CalendarActiveIcon className='w-5 h-5' />}
              placeholder={t('agent_dashboard.operation_log.search_bar.select_date_placeholder')}
              className='w-[11.3rem] h-9 font-normal'
            />
          )}
          endTrigger={(
            <div className='relative'>
              <label className='absolute bottom-9 left-0 text-sm font-medium text-[rgba(52,64,84,1)] mb-1.5 inline-block'>{t('agent_dashboard.operation_log.search_bar.end_date')}</label>
              <TextInput
                readOnly
                value={searchFormData.endDate}
                prefixIcon={<CalendarIcon className='w-5 h-5' />}
                prefixActiveIcon={<CalendarActiveIcon className='w-5 h-5' />}
                placeholder={t('agent_dashboard.operation_log.search_bar.select_date_placeholder')}
                className='w-[11.3rem] h-9 font-normal'
              />
            </div>
          )}
          onConfirm={(range) => {
            changeSearchForm('startDate', [formatDateToYYYYMMDD(range[0]), formatDateToYYYYMMDD(range[1])]);
          }}
        />
      </div>
      <div className='flex items-end space-x-3'>
        <TextButton
          label={t('agent_dashboard.operation_log.search_bar.reset')}
          className={cn(
            'max-w-max !px-5 h-9 bg-white',
            'font-semibold text-sm !text-[rgba(65,70,81,1)]',
            'border border-[rgba(213, 215, 218, 1)] hover:bg-gray-50',
          )}
          onClick={handleReset}
        />
        <TextButton
          label={t('agent_dashboard.operation_log.search_bar.search')}
          icon={<SearchBtnIcon className='w-5 h-5' />}
          className={cn(
            '!w-[6.4rem] h-9 ',
            'text-white font-semibold text-sm',
          )}
          onClick={handleSearch}
        />
      </div>
    </div>
  )
}
